(() => {
  const initProduct = (root) => {
    if (!root || root.dataset.poleneProductReady === 'true') return;
    root.dataset.poleneProductReady = 'true';

    root.querySelectorAll('[data-polene-tabs]').forEach((tabs) => {
      const buttons = [...tabs.querySelectorAll('[data-polene-tab]')];
      const panels = [...tabs.querySelectorAll('[data-polene-panel]')];

      const activate = (button, focus = false) => {
        const target = button.dataset.poleneTab;
        buttons.forEach((item) => {
          const active = item === button;
          item.classList.toggle('is-active', active);
          item.setAttribute('aria-selected', active ? 'true' : 'false');
          item.tabIndex = active ? 0 : -1;
        });
        panels.forEach((panel) => {
          const active = panel.dataset.polenePanel === target;
          panel.classList.toggle('is-active', active);
          panel.hidden = !active;
        });
        if (focus) button.focus();
      };

      buttons.forEach((button, index) => {
        button.addEventListener('click', () => activate(button));
        button.addEventListener('keydown', (event) => {
          if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
          event.preventDefault();
          let next = index;
          if (event.key === 'ArrowRight') next = (index + 1) % buttons.length;
          if (event.key === 'ArrowLeft') next = (index - 1 + buttons.length) % buttons.length;
          if (event.key === 'Home') next = 0;
          if (event.key === 'End') next = buttons.length - 1;
          activate(buttons[next], true);
        });
      });
    });

    root.querySelectorAll('[data-polene-colors]').forEach((colors) => {
      const viewport = colors.querySelector('[data-polene-colors-viewport]');
      const previous = colors.querySelector('[data-polene-colors-prev]');
      const next = colors.querySelector('[data-polene-colors-next]');
      if (!viewport) return;
      const amount = () => Math.max(164, viewport.clientWidth * 0.65);
      previous?.addEventListener('click', () => viewport.scrollBy({ left: -amount(), behavior: 'smooth' }));
      next?.addEventListener('click', () => viewport.scrollBy({ left: amount(), behavior: 'smooth' }));
      colors.querySelector('.is-active')?.scrollIntoView({ behavior: 'instant', block: 'nearest', inline: 'center' });
    });

    root.querySelectorAll('[data-polene-color-modal-open]').forEach((opener) => {
      const modalId = opener.dataset.poleneColorModalOpen;
      const colorModal = modalId ? document.getElementById(modalId) : null;
      if (!colorModal || colorModal.dataset.poleneColorModalReady === 'true') return;
      colorModal.dataset.poleneColorModalReady = 'true';

      const closeButton = colorModal.querySelector('[data-polene-color-modal-close]');
      const tabs = [...colorModal.querySelectorAll('[data-polene-color-filter]')];
      const cards = [...colorModal.querySelectorAll('[data-polene-color-card]')];
      const closeColorModal = (restoreFocus = true) => {
        if (typeof colorModal.close === 'function' && colorModal.open) colorModal.close();
        else colorModal.removeAttribute('open');
        opener.setAttribute('aria-expanded', 'false');
        document.documentElement.classList.remove('polene-modal-open');
        if (restoreFocus) opener.focus();
      };
      const filterCards = (filter, focus = false) => {
        tabs.forEach((tab) => {
          const active = tab.dataset.poleneColorFilter === filter;
          tab.classList.toggle('is-active', active);
          tab.setAttribute('aria-selected', active ? 'true' : 'false');
          tab.tabIndex = active ? 0 : -1;
          if (active && focus) tab.focus();
        });
        cards.forEach((card) => {
          const visible = filter === 'all' || card.dataset.poleneColorMaterial === filter;
          card.hidden = !visible;
        });
      };

      opener.addEventListener('click', () => {
        if (typeof colorModal.showModal === 'function' && !colorModal.open) colorModal.showModal();
        else colorModal.setAttribute('open', '');
        opener.setAttribute('aria-expanded', 'true');
        document.documentElement.classList.add('polene-modal-open');
        window.requestAnimationFrame(() => closeButton?.focus());
      });
      closeButton?.addEventListener('click', () => closeColorModal());
      colorModal.addEventListener('click', (event) => {
        if (event.target === colorModal) closeColorModal();
      });
      colorModal.addEventListener('cancel', (event) => {
        event.preventDefault();
        closeColorModal();
      });
      colorModal.addEventListener('close', () => {
        opener.setAttribute('aria-expanded', 'false');
        document.documentElement.classList.remove('polene-modal-open');
      });
      tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => filterCards(tab.dataset.poleneColorFilter));
        tab.addEventListener('keydown', (event) => {
          if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
          event.preventDefault();
          let nextIndex = index;
          if (event.key === 'ArrowRight') nextIndex = (index + 1) % tabs.length;
          if (event.key === 'ArrowLeft') nextIndex = (index - 1 + tabs.length) % tabs.length;
          if (event.key === 'Home') nextIndex = 0;
          if (event.key === 'End') nextIndex = tabs.length - 1;
          filterCards(tabs[nextIndex].dataset.poleneColorFilter, true);
        });
      });
      filterCards('all');
    });

    root.querySelectorAll('[data-polene-product-gallery]').forEach((gallery) => {
      const track = gallery.querySelector('[data-polene-product-track]');
      const slides = [...gallery.querySelectorAll('[data-polene-product-slide]')];
      const dots = [...gallery.querySelectorAll('[data-polene-product-dot]')];
      if (!track || !slides.length) return;

      const setActive = (index) => {
        dots.forEach((dot, dotIndex) => dot.classList.toggle('is-active', dotIndex === index));
      };

      dots.forEach((dot, index) => {
        dot.addEventListener('click', () => {
          slides[index]?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
          setActive(index);
        });
      });

      let ticking = false;
      track.addEventListener('scroll', () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(() => {
          const index = Math.round(track.scrollLeft / Math.max(1, track.clientWidth));
          setActive(Math.min(slides.length - 1, Math.max(0, index)));
          ticking = false;
        });
      }, { passive: true });
    });

    const modal = root.querySelector('[data-polene-product-modal]');
    const close = root.querySelector('[data-polene-product-modal-close]');
    const openers = root.querySelectorAll('[data-polene-product-modal-open]');
    if (modal) {
      const openModal = () => {
        if (typeof modal.showModal === 'function') modal.showModal();
        else modal.setAttribute('open', '');
        document.documentElement.classList.add('polene-modal-open');
      };
      const closeModal = () => {
        if (typeof modal.close === 'function') modal.close();
        else modal.removeAttribute('open');
        document.documentElement.classList.remove('polene-modal-open');
      };
      openers.forEach((opener) => opener.addEventListener('click', openModal));
      close?.addEventListener('click', closeModal);
      modal.addEventListener('click', (event) => {
        if (event.target === modal) closeModal();
      });
      modal.addEventListener('close', () => document.documentElement.classList.remove('polene-modal-open'));
    }
  };

  const initAll = (scope = document) => {
    scope.querySelectorAll?.('.polene-product').forEach(initProduct);
  };

  initAll();
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
